// hooks/market/useCreateProduct.ts
//
// Crée un produit dans une boutique dont l'utilisateur est owner.
// FORMULAIRE COMPLET (patch V10) : accepte tous les champs pertinents de
// market_products dès la création — pas seulement nom + prix. Seuls `name`
// et `price` restent obligatoires ; `category_id` reste optionnel (le
// backend retombe silencieusement sur "Autre" si absent, patch V8) ; tous
// les autres champs sont optionnels et ignorés côté backend s'ils sont
// absents (les défauts DB s'appliquent alors).
//
// Champs volontairement absents de CreateProductFields (voir
// backend/handleMarket.ts → createProduct pour le détail) :
//   - slug   → dérivé automatiquement du nom, jamais fourni par le client
//   - status → toujours forcé à 'draft' à la création
//   - disabled_reason → réservé à l'admin
//
// Appelle le vrai backend :
//   POST {API_BASE_URL}/market   action: 'create-product', { shop_id, ...fields }
// (voir backend/handleMarket.ts → createProduct)

import { useState, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';

export type ProductCondition = 'new' | 'used' | 'refurbished';

export interface CreateProductFields {
  // Obligatoires
  name: string;
  price: number;

  // Catégorisation
  category_id?: string; // optionnel — fallback "Autre" côté backend si absent
  sku?: string;          // optionnel, unique par boutique si fourni

  // Description
  description_short?: string;
  description?: string;
  brand?: string;
  condition?: ProductCondition; // défaut DB : 'new'

  // Images
  cover_image_url?: string;
  image_2_url?: string;
  image_3_url?: string; // nécessite image_2_url (sinon rejeté par le backend)

  // Prix & stock
  promo_price?: number;     // doit être ≤ price
  currency_code?: string;   // format ISO 3 lettres majuscules, défaut DB : 'XOF'
  stock_quantity?: number;  // ≥ 0, défaut DB : 0
  is_featured?: boolean;

  // Bouton WhatsApp (produit) — nécessite whatsapp_business_phone_override
  // si activé, sinon le backend rejette la création.
  show_whatsapp_business_button?: boolean;
  whatsapp_business_phone_override?: string;

  // Autres boutons d'action — chaque show_* nécessite son champ associé
  // si activé (validé côté backend, message d'erreur clair sinon).
  show_platform_chat_button?: boolean; // défaut DB : true
  show_buy_button?: boolean;
  buy_url?: string;
  show_learn_more_button?: boolean;
  learn_more_url?: string;
  show_call_button?: boolean;
  call_phone?: string;

  // SEO / partage social
  seo_title?: string;
  seo_description?: string;
  og_image_url?: string;

  // Données libres additionnelles
  extra_data?: Record<string, any>;
}

export type CreatedProduct = {
  id: string;
  name: string;
  slug: string;
  price: number;
  status: string;
  created_at: string;
};

export function useCreateProduct() {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const createProduct = useCallback(async (
    shopId: string,
    fields: CreateProductFields
  ): Promise<CreatedProduct | null> => {
    setLoading(true);
    setError(null);

    // Validations rapides côté client — mêmes règles que le backend, pour
    // éviter un aller-retour réseau inutile sur une erreur évidente. Le
    // backend reste la source de vérité finale (validations rejouées
    // là-bas de toute façon).
    if (!fields.name?.trim()) {
      setError('Le nom du produit est requis');
      setLoading(false);
      return null;
    }
    if (fields.price == null || isNaN(Number(fields.price)) || Number(fields.price) < 0) {
      setError('Un prix valide (≥ 0) est requis');
      setLoading(false);
      return null;
    }
    if (fields.promo_price != null && Number(fields.promo_price) > Number(fields.price)) {
      setError('Le prix promotionnel ne peut pas être supérieur au prix normal');
      setLoading(false);
      return null;
    }
    if (fields.image_3_url && !fields.image_2_url) {
      setError('image_3_url nécessite que image_2_url soit également fournie');
      setLoading(false);
      return null;
    }
    if (fields.show_buy_button && !fields.buy_url?.trim()) {
      setError('Un lien est requis pour activer le bouton "Acheter"');
      setLoading(false);
      return null;
    }
    if (fields.show_learn_more_button && !fields.learn_more_url?.trim()) {
      setError('Un lien est requis pour activer le bouton "En savoir plus"');
      setLoading(false);
      return null;
    }
    if (fields.show_call_button && !fields.call_phone?.trim()) {
      setError('Un numéro est requis pour activer le bouton "Appeler"');
      setLoading(false);
      return null;
    }
    if (fields.show_whatsapp_business_button && !fields.whatsapp_business_phone_override?.trim()) {
      setError('Un numéro WhatsApp est requis pour activer ce bouton au niveau du produit');
      setLoading(false);
      return null;
    }

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return null;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'create-product',
          access_token: token,
          shop_id:      shopId,
          ...fields,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de créer le produit');
        return null;
      }

      return data.product as CreatedProduct;
    } catch (err) {
      console.error('[useCreateProduct]', err);
      setError('network_error');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createProduct, loading, error };
}
